import { chunk, pick } from 'lodash-es';

import {
  httpDeleteUsersByUserIdPhoto,
  httpGetGroups,
  httpGetUsersBasic,
  httpGetUsersByUserId,
  httpPatchUsersByUserId,
  httpPatchUsersCurrent,
  httpPostUsersByUserIdPhoto,
  httpPostUsersCurrentPhoto,
  type UsersUserElasticSchemaWithId,
  type UsersUserSchemaPartial,
} from '@iconik-io/iconik-api/src/services/users';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import type { Entities } from '@iconik-io/shared-lib/src/redux/entities/types';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';

import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch, GetState } from '../../store/types';
import { selectUserRoles } from '../../utils/get_user_roles';
import urlAppendTimestamp from '../../utils/url_append_timestamp';
import { errorMessage, successMessage } from '../messages/thunks';
import { selectIsSharePage } from '../share/selectors';
import { setUsersDefaultPrimaryGroup } from './slice';

export function getUser(id = 'current') {
  return (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(httpGetUsersByUserId(id));
}

export function updateUserPhoto(
  userId: string | undefined,
  data: {
    photo: Blob;
  },
) {
  const formData = new FormData();
  formData.append('photo', data.photo, 'useravatar.jpg');
  return (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const id = userId ?? getState().authReducer.userData.id;
    return dispatch(
      userId
        ? httpPostUsersByUserIdPhoto(id, formData)
        : httpPostUsersCurrentPhoto(formData),
    ).then((response) => {
      const photoData = response.body;
      if (!photoData.photo || !photoData.photo_big || !photoData.photo_small) {
        return;
      }
      dispatch(
        updateEntities({
          users: {
            [id]: {
              photo: urlAppendTimestamp(photoData.photo),
              photo_big: urlAppendTimestamp(photoData.photo_big),
              photo_small: urlAppendTimestamp(photoData.photo_small),
            },
          },
        }),
      );
    });
  };
}

export function updatePassword(
  id: string | undefined,
  data: UsersUserSchemaPartial,
) {
  return (dispatch: AppDispatch) => {
    return dispatch(
      id ? httpPatchUsersByUserId(id, data) : httpPatchUsersCurrent(data),
    ).then(() => {
      dispatch(successMessage({ text: 'Password Updated' }));
    });
  };
}

export function updateUser(
  id: string | undefined,
  data: UsersUserSchemaPartial,
) {
  return (dispatch: AppDispatch): void => {
    dispatch(
      id ? httpPatchUsersByUserId(id, data) : httpPatchUsersCurrent(data),
    ).catch((error) => {
      // 403 errors are suppressed by default in src/middleware/http_error_handler.ts
      // but it still should be shown in this scenario
      if (error.response?.status === 403) {
        dispatch(errorMessage({ text: handleErrors(error) }));
      }
    });
  };
}

let usersIds: string[] = [];
let timeout: number | undefined;

export function getBasicUser(id: string) {
  // a timer is used to gather multiple action calls in just one request
  return (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();
    const userRoles = selectUserRoles(state);
    const isSharePage = selectIsSharePage(state);
    const allowedToFetchUsers = isSharePage
      ? userRoles.canListAllUsers
      : userRoles.canListAllUsers || userRoles.canListGroupUsers;
    if (!allowedToFetchUsers) {
      return;
    }

    // check if is to be requested or requested already
    if (!usersIds.includes(id) && !state.entities.users_basic[id]) {
      usersIds.push(id);
    }

    // when the time is completed request to the server and dispatch action
    if (!timeout && usersIds.length > 0) {
      timeout = window.setTimeout(() => {
        clearTimeout(timeout);
        timeout = undefined;

        const userIdsChunks = chunk(usersIds, 50);
        const promises = [];
        for (const userIdsChunk of userIdsChunks) {
          promises.push(
            dispatch(
              httpGetUsersBasic({ ids: userIdsChunk.toString(), per_page: 50 }),
            ),
          );
        }

        Promise.all(promises).then(() => {
          usersIds = [];
        });
      }, 50);
    }
  };
}

const requestBasicUsersByIds = (ids: string[]) => (dispatch: AppDispatch) =>
  dispatch(httpGetUsersBasic({ ids: ids.join(',') })).then(({ meta }) => {
    const users: Record<string, UsersUserElasticSchemaWithId> =
      meta?.normalizedMultiple?.entities.users_basic ?? {};
    return users;
  });

// This function's destiny is to replace getBasicUser()
export const askForBasicUsersByIds =
  (ids: string[] = []) =>
  (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<Entities<UsersUserElasticSchemaWithId>> => {
    const usersBasic = getState().entities.users_basic;
    const usersToFetch = ids.filter((id) => !usersBasic[id]);
    if (usersToFetch.length) {
      return dispatch(requestBasicUsersByIds(ids));
    }

    return Promise.resolve(pick(usersBasic, ids));
  };

export const clearUserPhoto =
  () =>
  (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const userId =
      getCurrentRouteParams().id ?? getState().authReducer.userData.id;
    return dispatch(httpDeleteUsersByUserIdPhoto(userId)).then(() => {
      dispatch(
        updateEntities({
          users: {
            [userId]: {
              photo: '',
              photo_big: '',
              photo_small: '',
            },
          },
        }),
      );
    });
  };

export function getUserDefaultPrimaryGroup() {
  return (dispatch: AppDispatch): void => {
    dispatch(httpGetGroups({ query: 'everyone' })).then((response) => {
      if (response.body.objects.length) {
        dispatch(setUsersDefaultPrimaryGroup(response.body.objects[0].id));
      }
    });
  };
}
